import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { AgeUnit, ChartId, VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AxisConfiguration } from "@/Patient/Models/GrowthCharts/ChartData";
import { CurvesName } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { GrowthChartModel } from "./GrowthChartModel";

abstract class Age0To2GrowthChart extends GrowthChartModel {
    public isDisplayedByDefault(): boolean {
        const correctedAge = this.getCorrectedAge();
        return (LuxonDateHelper.dateDiff(this.patient.DateOfBirth, DateTime.nowDate(), "years") <= 2 && !correctedAge)
            || (correctedAge != null && correctedAge >= 0 && correctedAge <= 2);
    }

    public isCompatible(): boolean {
        return super.isCompatible() && this.vitalSigns
            .filter(vital => !!vital[this.vitalProperty])
            .some(vital => this.filterVitalSign(vital));
    }

    public filterVitalSign(vitalSign: MedicalItemVitalSign): boolean {
        if (!this.patient.DateOfBirth)
            return false;

        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, "months") <= 24;
    }

    public get isVitalSign(): boolean {
        return false;
    }

    public get ageUnit(): AgeUnit {
        return "month";
    }
}

class Age0To2WeightForAge extends Age0To2GrowthChart {
    public get id(): ChartId {
        return "WFA02";
    }
    public get name(): string {
        return cacheStore.GetString("Age0To2WeightForAgeLabel");
    }
    public get shortName(): string {
        return cacheStore.GetString("Age0To2WeightForAgeShortLabel");
    }

    public get yLabel(): string {
        return cacheStore.GetString("WeightLabel")
            + ` (${this.unit === UnitSystem.Metric
                ? cacheStore.GetString("KilogramUnitSymbolLabel")
                : cacheStore.GetString("PoundUnitSymbolLabel")})`;
    }
    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "weightMale0-2" : "weightFemale0-2";
    }
    public get xAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("AgeLabel")} (${cacheStore.GetString("MonthAbbrevLabel")})`,
            min: 0,
            max: 24,
            interval: 1
        };
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("KilogramUnitSymbolLabel")})`,
                min: 0,
                max: 16,
                interval: 1
            }
            : {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("PoundUnitSymbolLabel")})`,
                min: 0,
                max: 36,
                interval: 2
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Weight";
    }
}

class Age0To2LengthForAge extends Age0To2GrowthChart {
    public get id(): ChartId {
        return "LFA02";
    }
    public get name(): string {
        return cacheStore.GetString("Age0To2LengthForAgeLabel");
    }
    public get shortName(): string {
        return cacheStore.GetString("Age0To2LengthForAgeShortLabel");
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "lengthMale0-2" : "lengthFemale0-2";
    }
    public get xAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("AgeLabel")} (${cacheStore.GetString("MonthAbbrevLabel")})`,
            min: 0,
            max: 24,
            interval: 1
        };
    }
    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: cacheStore.GetString("LengthLabel") + ` (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 45,
                max: 95,
                interval: 5
            }
            : {
                label: cacheStore.GetString("LengthLabel") + ` (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 16,
                max: 38,
                interval: 5
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Height";
    }
}

class Age0To2WeightForLength extends Age0To2GrowthChart {
    public get id(): ChartId {
        return "WFL02";
    }

    public get name(): string {
        return cacheStore.GetString("Age0To2WeightForLengthLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("Age0To2WeightForLengthShortLabel");
    }

    public isCompatible(): boolean {
        return super.isCompatible() && this.vitalSigns.some(vital => this.filterVitalSign(vital));
    }

    public filterVitalSign(vitalSign: MedicalItemVitalSign): boolean {
        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, "months") <= 24 &&
            !!vitalSign.Weight &&
            !!vitalSign.Height &&
            this.convertVitalSignToCurrentUnitSystem("Height", vitalSign.Height, vitalSign.WeightUnitLookupId, vitalSign.HeightUnitLookupId, vitalSign.HeadCircumferenceUnitLookupId) >= this.xAxis.min &&
            this.convertVitalSignToCurrentUnitSystem("Height", vitalSign.Height, vitalSign.WeightUnitLookupId, vitalSign.HeightUnitLookupId, vitalSign.HeadCircumferenceUnitLookupId) <= this.xAxis.max;
    }

    public get yLabel(): string {
        return cacheStore.GetString("WeightLabel")
            + ` (${this.unit === UnitSystem.Metric
                ? cacheStore.GetString("KilogramUnitSymbolLabel")
                : cacheStore.GetString("PoundUnitSymbolLabel")})`;
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "weightForLenghtMale0-2" : "weightForLenghtFemale0-2";
    }

    public get xAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: cacheStore.GetString("LengthLabel") + ` (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 45,
                max: 110,
                interval: 5
            }
            : {
                label: cacheStore.GetString("LengthLabel") + ` (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 18,
                max: 42,
                interval: 2
            };
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("KilogramUnitSymbolLabel")})`,
                min: 0,
                max: 23,
                interval: 2
            }
            : {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("PoundUnitSymbolLabel")})`,
                min: 0,
                max: 50,
                interval: 5
            };
    }

    public get hasLengthAsIndependentAxis(): boolean {
        return true;
    }

    public get isPlotSelectionSupported(): boolean {
        return false;
    }

    public get vitalProperty(): VitalProperty {
        return "Weight";
    }

    public get ageUnit(): AgeUnit {
        return null;
    }
}

export { Age0To2LengthForAge, Age0To2WeightForAge, Age0To2WeightForLength };
